import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyProjects } from '@features/dashboard/services/dashboard.service';
import { useMySessions } from '@features/sessions/services/sessions.service';
import type { MyProjectItem } from '@features/projects/types/project.types';
import type { SessionMineItem } from '@features/sessions/types/session.types';
import { sessionTitle } from '../utils/labels';
import { buildIndex } from '../utils/search';

export interface SearchCorpus {
  readonly projects: readonly MyProjectItem[];
  readonly sessions: readonly SessionMineItem[];
  /** Folded and weighted, ready for `searchIndex`. */
  readonly index: ReturnType<typeof buildIndex>;
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly refetch: () => void;
}

/**
 * The two lists the rail and the composer both read.
 *
 * One hook for both so they share TanStack Query's cache rather than each asking
 * for the same pages: mounting the rail and typing in the composer is two
 * readers of one fetch, not two fetches.
 */
/** Stable empty fallbacks. A fresh `[]` per render would change the memo's
 *  dependencies every time and rebuild the index on each keystroke. */
const NO_PROJECTS: readonly MyProjectItem[] = [];
const NO_SESSIONS: readonly SessionMineItem[] = [];

export function useSearchCorpus(): SearchCorpus {
  const { t } = useTranslation();
  const projectsQuery = useMyProjects();
  const sessionsQuery = useMySessions();

  const projects = projectsQuery.data ?? NO_PROJECTS;
  const sessions = sessionsQuery.data ?? NO_SESSIONS;

  const index = useMemo(
    () =>
      buildIndex(projects, sessions, {
        session: (session) => sessionTitle(session, t),
      }),
    [projects, sessions, t],
  );

  return {
    projects,
    sessions,
    index,
    isPending: projectsQuery.isPending || sessionsQuery.isPending,
    isError: projectsQuery.isError || sessionsQuery.isError,
    refetch: () => {
      void projectsQuery.refetch();
      void sessionsQuery.refetch();
    },
  };
}
